import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Box, Text, Anchor,
} from 'grommet';
import { Waypoint } from 'grommet-icons';
import ReactTooltip from 'react-tooltip';
import { useAppTheme } from '@Components/Context';
import { CellDropMenu } from '@Components/Partial/Product/Dashboard';
import {
  SimpleModal, CopyButton, DropButton, AppButton,
} from '@Components/Control';

const StyledExtraUrlButton = styled(Box)`
  text-align: start;
  background-color: var(--button-table-bg);
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 0.25rem 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--button-table-text);
  width: fit-content;

  @media (max-width: 768px) {
    padding: 0.35rem 0.6rem;
  }

  &:hover {
    cursor: pointer;
    border: 1px solid ${({ focusHighlight }) => focusHighlight};
  }

  ${({ isOpen, focusHighlight }) => isOpen && `
    border: 1px solid ${focusHighlight};
  `}
`;

const StyledPagedTableUrl = styled(Text)`
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow:hidden;
  -o-text-overflow: ellipsis;
  text-overflow: ellipsis;
  display: -webkit-box;

  @media (max-width: 48em) {
    -webkit-line-clamp: 3;
  }
`;

const StyledAnchor = styled(Anchor)`
  text-decoration: none;
  color: inherit;

  &:hover {
    cursor: pointer;
    text-decoration: underline;
    color: ${({ highlight }) => highlight};

    svg {
      fill: ${({ highlight }) => highlight};
      stroke: ${({ highlight }) => highlight};
    }
  }

  .visitTooltip {
    font-family: inherit;
    border-radius: 4px;
    background-color: ${({ tooltipColor }) => tooltipColor};
  }
`;

const DomainDetails = ({
  small,
  handleDomainUpdate,
  dmcaListsEnabled = false,
  modalTitle = 'Domain details',
  primaryUrl,
  extraUrls = null,
  includeISPData = false,
  hostingData = null,
}) => {
  const [showModal, setShowModal] = React.useState(false);
  const [dropOpen, setDropOpen] = React.useState(false);
  const { theme: { page, button } } = useAppTheme();

  const cleanUrl = (fullUrl) => {
    if (!fullUrl) return { domainWithPath: '', rootDomain: '' };

    let urlObj;
    try {
      urlObj = new URL(fullUrl);
    } catch {
      return { domainWithPath: fullUrl, rootDomain: '' };
    }

    const rootDomain = urlObj.hostname.replace(/^www\./, '');
    const domainWithPath = rootDomain + urlObj.pathname + urlObj.search + urlObj.hash;

    return { domainWithPath, rootDomain };
  };

  const renderRowData = (label = null, url) => (
    <Box wrap direction="row" gap="0.5rem" align="center" key={url}>
      {label && (<Text size="1rem" color={page.modalText} weight={500}>{`${label}:`}</Text>)}
      <Text color={page.urlColor} size="1rem">{url}</Text>
      <Box direction="row" gap="0.5rem" pad={{ left: '0.25rem' }} align="center">
        <CopyButton
          hideTooltip={small}
          renderKey={url}
          value={url}
          color={page.modalText}
          hoverColor={page.highlight}
          tooltipColor={page.highlight}
          iconSize="1rem"
          tooltipPosition="bottom"
        />
        <StyledAnchor
          href={url}
          highlight={page.highlight}
          target="_blank"
          rel="noopener noreferrer"
          data-tip="Visit link"
          data-for={`${url}-visit-tooltip`}
          tooltipColor={page.highlight}
        >
          <Waypoint size="1rem" color={page.modalText} />
          {!small && (
            <ReactTooltip
              tooltip
              id={`${url}-visit-tooltip`}
              className="visitTooltip"
              place="bottom"
              arrowColor={page.highlight}
              effect="solid"
            />
          )}
        </StyledAnchor>
      </Box>
    </Box>
  );

  const { domainWithPath, rootDomain } = cleanUrl(primaryUrl);
  const matchedHost = includeISPData && hostingData?.length > 0
    ? hostingData.find((item) => {
      if (!item.host || !rootDomain) return false;
      return rootDomain === item.host || rootDomain.endsWith(`.${item.host}`);
    }) || null
    : null;
  const domainOptions = [
    { display: 'Add domain to DMCA denylist', api: 'dmca-denylist' },
    { display: 'Add domain to DMCA allowlist', api: 'dmca-allowlist' },
  ];

  const renderIspData = () => {
    const noDataMessage = (
      <Text size="0.9rem" color={page.secondaryText || page.modalText}>
        {`No hosting data available for ${rootDomain || 'this domain'}.`}
      </Text>
    );

    if (!matchedHost) return noDataMessage;

    const fields = [
      { label: 'IP Address', value: matchedHost.ipAddress || null },
      { label: 'IP Geolocation', value: matchedHost.ipGeolocation || null },
      { label: 'IP Timestamp', value: matchedHost.ipTimestamp || null },
      { label: 'ISP', value: matchedHost.internetServiceProviderName || null },
      {
        label: 'Global Ranking',
        value: matchedHost.globalRanking != null
          ? Number(matchedHost.globalRanking).toLocaleString('en-US')
          : null,
      },
      {
        label: 'Avg. Monthly Visitors',
        value: matchedHost.visitors != null
          ? Number(matchedHost.visitors).toLocaleString('en-US')
          : null,
      },
    ].filter(({ value }) => value != null);

    if (fields.length === 0) return noDataMessage;

    return fields.map(({ label, value }) => (
      <Box key={label} direction="row" gap="0.5rem" align="center">
        <Text size="1rem" color={page.modalText} weight={500}>{`${label}:`}</Text>
        <Text color={page.urlColor} size="1rem">{value}</Text>
      </Box>
    ));
  };

  return (
    <Box>
      <StyledExtraUrlButton
        onClick={() => setShowModal(true)}
        isOpen={showModal}
        focusHighlight={page.focusHighlight}
      >
        <StyledPagedTableUrl color={button.tableText} size="0.9rem">
          {domainWithPath}
        </StyledPagedTableUrl>
      </StyledExtraUrlButton>
      {showModal && (
        <SimpleModal
          hideLowerSection
          small={small}
          toggleModal={setShowModal}
          modalTitle={modalTitle}
          highlightColor={page.highlight}
          titleSize="1.2rem"
          desktopMinWidth="55rem"
        >
          <Box
            direction="column"
            gap="0.5rem"
            justify="start"
            align="start"
            width={{ min: 'fit-content' }}
            pad={{ vertical: '0.5rem' }}
          >
            {renderRowData('Full URL', primaryUrl)}
            <Box direction="row" gap="0.5rem" align="center">
              <Text size="1rem" color={page.modalText} weight={500}>
                Root domain:
              </Text>
              <Text color={page.urlColor} size="1rem">{rootDomain}</Text>
              {dmcaListsEnabled && (
                <Box width="fit-content" flex={{ grow: 1 }}>
                  <DropButton
                    onOpen={() => setDropOpen(true)}
                    onClose={() => setDropOpen(false)}
                    withCloseDropContent={(onClose) => (
                      <CellDropMenu
                        options={domainOptions}
                        onClick={(option) => {
                          handleDomainUpdate(option.api, rootDomain);
                          onClose();
                        }}
                      />
                    )}
                  >
                    {(Icon) => (
                      <AppButton
                        dropDown
                        tableButtonStyle
                        bgColor={button.tableBg}
                        color={button.tableText}
                        fontWeight={600}
                        label="Actions"
                        icon={<Icon color={button.tableIcon} size="1rem" />}
                        focusHighlight={page.focusHighlight}
                        isOpen={dropOpen}
                      />
                    )}
                  </DropButton>
                </Box>
              )}
            </Box>
            {extraUrls?.length > 0 && (
              <Box
                direction="column"
                width="100%"
                gap="0.5rem"
              >
                <Box
                  pad={{ top: '0.5rem' }}
                  width="fit-content"
                  border={{
                    side: 'bottom', width: '1px', style: 'solid', color: 'var(--nav-border)',
                  }}
                >
                  <Text size="1rem" color={page.modalText} weight={600}>
                    Additional URLs
                  </Text>
                </Box>
                {extraUrls.map((url) => renderRowData(url.label, url.url))}
              </Box>
            )}
            {includeISPData && hostingData !== null && (
              <Box direction="column" width="100%" gap="0.35rem" pad={{ top: '0.25rem' }}>
                <Box
                  pad={{ top: '0.5rem' }}
                  width="fit-content"
                  border={{
                    side: 'bottom', width: '1px', style: 'solid', color: 'var(--nav-border)',
                  }}
                >
                  <Text size="1rem" color={page.modalText} weight={600}>
                    ISP Information
                  </Text>
                </Box>
                {renderIspData()}
              </Box>
            )}
          </Box>
        </SimpleModal>
      )}
    </Box>
  );
};

DomainDetails.propTypes = {
  small: PropTypes.bool.isRequired,
  handleDomainUpdate: PropTypes.func.isRequired,
  dmcaListsEnabled: PropTypes.bool,
  modalTitle: PropTypes.string,
  primaryUrl: PropTypes.string.isRequired,
  extraUrls: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  })),
  includeISPData: PropTypes.bool,
  hostingData: PropTypes.arrayOf(PropTypes.shape({
    host: PropTypes.string,
    ipAddress: PropTypes.string,
    ipGeolocation: PropTypes.string,
    ipTimestamp: PropTypes.string,
    internetServiceProviderName: PropTypes.string,
    globalRanking: PropTypes.number,
    visitors: PropTypes.number,
  })),
};

export default DomainDetails;
